import {z} from "zod";
import {categoryTaxonomy} from "./categories.ts";
import type {Transaction} from "./finance.ts";

/**
 * Structured facts derived from provider transaction text by the enrichment
 * model. Every field is optional knowledge: `null` means "not determined",
 * never a guess. The raw provider fields stay untouched next to it.
 */
export const paymentChannels = ["card", "transfer", "direct_debit", "wallet", "cash", "check", "fee", "payroll", "other"] as const;
export type PaymentChannel = (typeof paymentChannels)[number];

export const transactionEnrichmentSchema = z.object({
  /** Normalized company display name, stable across message variants ("AMZN MKTP US*2K" -> "Amazon"). */
  companyName: z.string().min(1).max(120).nullable(),
  /** Canonical grouping key derived from the company name; lowercase, no punctuation. */
  companyKey: z.string().min(1).max(120).nullable(),
  companyDomain: z.string().min(3).max(120).nullable(),
  /** ISO 3166-1 alpha-2 country the merchant or charge is tied to. */
  countryCode: z
    .string()
    .regex(/^[A-Z]{2}$/)
    .nullable(),
  city: z.string().min(1).max(80).nullable(),
  paymentChannel: z.enum(paymentChannels).nullable(),
  isSubscription: z.boolean(),
  category: z.enum(categoryTaxonomy).nullable(),
  tags: z.array(z.string().min(1).max(40)).max(8),
  confidence: z.number().min(0).max(1),
  /** One model ID, or two IDs of up to 80 characters joined by '+' for hybrid enrichment. */
  model: z.string().max(161).nullable(),
  enrichedAt: z.string().datetime().nullable()
});
export type TransactionEnrichment = z.infer<typeof transactionEnrichmentSchema>;

/** Shape the model must return for one descriptor. Kept separate from storage metadata. */
export const enrichmentVerdictSchema = transactionEnrichmentSchema.omit({model: true, enrichedAt: true});
export type EnrichmentVerdict = z.infer<typeof enrichmentVerdictSchema>;

export const enrichmentBatchResponseSchema = z.object({
  results: z.array(z.object({descriptor: z.string().min(1).max(200), verdict: enrichmentVerdictSchema})).max(200)
});
export type EnrichmentBatchResponse = z.infer<typeof enrichmentBatchResponseSchema>;

export function emptyEnrichment(): TransactionEnrichment {
  return {companyName: null, companyKey: null, companyDomain: null, countryCode: null, city: null, paymentChannel: null, isSubscription: false, category: null, tags: [], confidence: 0, model: null, enrichedAt: null};
}

/** Deterministic normalization used to group rows by company when the model gave a name. */
export function normalizeCompanyKey(name: string): string {
  return name
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, " ")
    .trim()
    .replace(/\s+/g, "-");
}

/** Grouping label for one transaction: enriched company first, provider merchant next, cleaned text last. */
export function companyLabelOf(transaction: Pick<Transaction, "merchantName" | "normalizedDescription"> & {enrichment?: TransactionEnrichment | null | undefined}): string {
  return transaction.enrichment?.companyName ?? transaction.merchantName ?? transaction.normalizedDescription;
}

export function companyKeyOf(transaction: Pick<Transaction, "merchantName" | "normalizedDescription"> & {enrichment?: TransactionEnrichment | null | undefined}): string {
  const enriched = transaction.enrichment?.companyKey ?? (transaction.enrichment?.companyName ? normalizeCompanyKey(transaction.enrichment.companyName) : null);
  return enriched ?? normalizeCompanyKey(transaction.merchantName ?? transaction.normalizedDescription);
}

/** Descriptors that still need a model pass, deduplicated so cost scales with merchants, not rows. */
export function descriptorsNeedingEnrichment(transactions: ReadonlyArray<Transaction>): string[] {
  const pending = new Set<string>();
  for (const transaction of transactions) {
    if (transaction.enrichment?.enrichedAt) continue;
    pending.add(transaction.normalizedDescription || transaction.description);
  }
  return [...pending].filter((value) => value.trim().length > 0).sort();
}
